import { useState, useEffect } from 'react'
import { evaluationAPI } from '../../services/api'

export default function RubricEditor() {
  const [rubric, setRubric] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editJson, setEditJson] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [expandedDims, setExpandedDims] = useState(new Set())

  useEffect(() => {
    loadRubric()
  }, [])

  const loadRubric = async () => {
    setLoading(true)
    try {
      const data = await evaluationAPI.getRubric()
      setRubric(data)
    } catch {
      setError('Failed to load rubric')
    } finally {
      setLoading(false)
    }
  }

  const startEditing = () => {
    setEditJson(JSON.stringify(rubric, null, 2))
    setEditing(true)
    setError(null)
    setSuccess(false)
  }

  const saveRubric = async () => {
    setSaving(true)
    setError(null)
    try {
      const parsed = JSON.parse(editJson)
      await evaluationAPI.updateRubric({
        version: parsed.version || 'custom',
        name: parsed.name || 'custom',
        dimensions: parsed.dimensions || {},
        judge_config: parsed.judge_config || {},
      })
      setRubric(parsed)
      setEditing(false)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format')
      } else {
        setError(err.response?.data?.detail || 'Failed to save rubric')
      }
    } finally {
      setSaving(false)
    }
  }

  const toggleDim = (dim) => {
    setExpandedDims(prev => {
      const next = new Set(prev)
      if (next.has(dim)) next.delete(dim)
      else next.add(dim)
      return next
    })
  }

  if (loading) {
    return (
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body flex justify-center py-8">
          <span className="loading loading-spinner loading-sm"></span>
        </div>
      </div>
    )
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <div className="flex items-center justify-between">
          <h3 className="card-title text-lg">Evaluation Rubric</h3>
          <div className="flex gap-2">
            {!editing ? (
              <button className="btn btn-outline btn-sm" onClick={startEditing}>Edit</button>
            ) : (
              <>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={saveRubric} disabled={saving}>
                  {saving ? <span className="loading loading-spinner loading-xs"></span> : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>

        {error && <div className="alert alert-error text-sm mt-2"><span>{error}</span></div>}
        {success && <div className="alert alert-success text-sm mt-2"><span>Rubric saved successfully</span></div>}

        {editing ? (
          <textarea
            className="textarea textarea-bordered w-full mt-2 font-mono text-xs"
            rows={20}
            value={editJson}
            onChange={e => setEditJson(e.target.value)}
          />
        ) : rubric ? (
          <div className="mt-3 space-y-2">
            <div className="text-sm text-base-content/60">
              Version: {rubric.version} | Model: {rubric.judge_config?.model || 'N/A'}
            </div>

            {rubric.dimensions && Object.entries(rubric.dimensions).map(([key, dim]) => (
              <div key={key} className="collapse collapse-arrow bg-base-200 rounded-lg">
                <input
                  type="checkbox"
                  checked={expandedDims.has(key)}
                  onChange={() => toggleDim(key)}
                />
                <div className="collapse-title text-sm font-medium py-2 min-h-0">
                  {dim.name || key} — <span className="text-base-content/60">{dim.description}</span>
                </div>
                <div className="collapse-content">
                  <div className="space-y-1 text-xs">
                    {dim.scores && Object.entries(dim.scores).map(([score, desc]) => (
                      <div key={score} className="flex gap-2">
                        <span className="badge badge-sm badge-outline w-6 text-center">{score}</span>
                        <span>{desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-base-content/50 mt-2">No rubric loaded.</p>
        )}
      </div>
    </div>
  )
}
